import classNames from 'classnames';
import * as inflection from 'inflection';
import React, { Fragment, useEffect, useState } from 'react';
import Button from '../../components/Button';
import styles from './FoldedComments.module.scss';

interface FoldedCommentsProps<T extends { id: string }> {
  isExpanded?: boolean;
  limit?: number;
  comments: T[];
  renderComment: (
    comment: T,
    index: number,
    isFolded: boolean,
  ) => React.ReactNode;
  onUnfold?: () => void;
}

const FoldedComments = <T extends { id: string }>({
  isExpanded = true,
  limit = 2,
  comments,
  renderComment,
  onUnfold = () => {},
}: FoldedCommentsProps<T>) => {
  const [isFolded, setIsFolded] = useState(true);

  useEffect(() => {
    if (!isExpanded && !isFolded) {
      setIsFolded(true);
    }
  }, [isExpanded, isFolded]);

  const handleUnfold = () => {
    setIsFolded(false);
    onUnfold();
  };

  const moreCount = comments.length - limit;
  const moreText =
    isFolded && moreCount > 0
      ? `${moreCount} more ${inflection.inflect('comment', moreCount)}`
      : null;

  return (
    <div className="comments">
      {comments.map((comment, idx) => {
        if (moreText) {
          if (idx === 0) {
            return (
              <Fragment key={comment.id}>
                {renderComment(comment, idx, !isExpanded)}
                <div
                  className={classNames(
                    'comment-more-count',
                    styles.container,
                    { [styles.folded]: !isExpanded },
                  )}
                >
                  {isExpanded ? (
                    <Button variant="link" size="micro" onClick={handleUnfold}>
                      {moreText}
                    </Button>
                  ) : (
                    <div className={styles.link}>{moreText}</div>
                  )}
                </div>
              </Fragment>
            );
          }
          if (idx < comments.length - limit + 1) {
            return null;
          }
        }

        return (
          <Fragment key={comment.id}>
            {renderComment(comment, idx, !isExpanded && !!moreText)}
          </Fragment>
        );
      })}
    </div>
  );
};

export default FoldedComments;
